// =============================================================
// mode — strength/yoga mode toggle (parallels theme.ts)
// =============================================================
//
// - Strength is the default (no `data-mode` attribute = strength tokens).
// - Yoga is set via `data-mode="yoga"` on <html>, swapping the same
//   --color-accent* tokens that 50+ call-sites in site.css already consume.
// - Persists to localStorage. The inline boot script in index.html reads
//   the same key BEFORE CSS paints so the first frame is already in the
//   right palette — no flash of wrong colors.
// - `onModeChange()` lets the brand-flip button (and any other UI) re-
//   render when the user flips the mode.

export type Mode = 'strength' | 'yoga';

const STORAGE_KEY = 'studio333:mode';
const ATTR = 'data-mode';

const isMode = (s: string): s is Mode => s === 'strength' || s === 'yoga';

const subscribers = new Set<() => void>();

// ---------- Reading the initial mode (localStorage > DOM > default) ----------
// The inline boot script in index.html already applies `data-mode="yoga"`
// synchronously before paint. By the time this module loads, the DOM
// attribute is already correct — we just mirror it into JS state so
// `getMode()` returns the right value.
export function detectInitial(): Mode {
  if (typeof document !== 'undefined') {
    const attr = document.documentElement.getAttribute(ATTR);
    if (attr && isMode(attr)) return attr;
  }
  return 'strength';
}

// ---------- Subscription ----------
export function onModeChange(cb: () => void): () => void {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
}

// ---------- State ----------
let _mode: Mode = 'strength';

// `meta[name="theme-color"]` is read by the UA (mobile Safari / Chrome
// chrome) before CSS variables resolve, so it MUST be a literal hex — not
// a CSS var. Strength yellow vs yoga light purple.
const THEME_COLOR: Record<Mode, string> = {
  strength: '#ffd601',
  yoga: '#b794d4',
};

function syncThemeColorMeta(m: Mode): void {
  if (typeof document === 'undefined') return;
  const meta = document.head.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', THEME_COLOR[m]);
}

// Favicon swap — the UA does not reload the SVG when only the file content
// changes, so we cache-bust with a mode key (`?v=yoga` / `?v=strength`). The
// strength variant is the static `index.html` link; JS rewrites the href here.
const FAVICON: Record<Mode, string> = {
  strength: '/favicon.svg',
  yoga: '/favicon-yoga.svg',
};

function syncFaviconLink(m: Mode): void {
  if (typeof document === 'undefined') return;
  const link = document.head.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) return;
  link.setAttribute('href', `${FAVICON[m]}?v=${m}`);
}

/** Apply the given mode to <html> and notify subscribers. */
function applyMode(next: Mode): void {
  if (next === _mode) return;
  _mode = next;
  if (typeof document !== 'undefined') {
    if (next === 'strength') {
      // Default — drop the attribute so :root tokens win without us
      // having to redefine a `[data-mode="strength"]` block.
      document.documentElement.removeAttribute(ATTR);
    } else {
      document.documentElement.setAttribute(ATTR, next);
    }
  }
  syncThemeColorMeta(next);
  syncFaviconLink(next);
  try { localStorage.setItem(STORAGE_KEY, next); } catch { /* noop */ }
  subscribers.forEach(cb => {
    try { cb(); } catch (err) {
      console.error('[mode] subscriber threw during applyMode:', err);
    }
  });
}

// ---------- Public API ----------
export function getMode(): Mode {
  return _mode;
}

export function setMode(next: Mode): void {
  applyMode(next);
}

/** Flip strength ↔ yoga. Convenience for the brand-num toggle. */
export function toggleMode(): Mode {
  applyMode(_mode === 'yoga' ? 'strength' : 'yoga');
  return _mode;
}

/** Read the persisted mode without applying it. */
export function readPersistedMode(): Mode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && isMode(stored)) return stored;
  } catch { /* SSR / private mode */ }
  return 'strength';
}

/** Initialise JS state from the DOM (which the inline boot script
 *  already set). Call once after the boot script has run. */
export function initMode(): Mode {
  _mode = detectInitial();
  syncThemeColorMeta(_mode);
  syncFaviconLink(_mode);
  return _mode;
}

// ---- Member-discipline authority ----
//
// When a member is logged in, their `discipline` is the source of truth
// for which site flavor (`strength` | `yoga`) should be active. This
// helper tells callers whether to re-assert the mode on every mode
// change. The cached user lives in localStorage under the same key as
// `getCachedUser()` in `userApi.ts` (`studio333:user`) — we read it
// directly here to avoid a circular import.
//
// Returns false when no user is logged in, or when the cached user
// pre-dates the discipline field (legacy / SSR-ish / private mode).
export function isModeAuthoritative(): boolean {
  if (typeof localStorage === 'undefined') return false;
  try {
    const raw = localStorage.getItem('studio333:user');
    if (!raw) return false;
    const u = JSON.parse(raw) as { discipline?: unknown };
    return u?.discipline === 'strength' || u?.discipline === 'yoga';
  } catch {
    return false;
  }
}

/** Read the discipline of the cached user, if any. */
export function getCachedDiscipline(): 'strength' | 'yoga' | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem('studio333:user');
    if (!raw) return null;
    const u = JSON.parse(raw) as { discipline?: unknown };
    if (u?.discipline === 'yoga') return 'yoga';
    if (u?.discipline === 'strength') return 'strength';
    return null;
  } catch {
    return null;
  }
}
